import {Anchor, createStyles} from '@mantine/core';
import React, {useCallback} from 'react';

import navigateToURL from 'lib/navigateToURL';
import {ChatSettingsTab} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {ACTIVITY_MODAL_KEY} from 'llm/chat-library/tool-details/ActivityModal';
import {chatSettingsModalUrl} from 'llm/routes/UrlUtils';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';

// A left-aligned action link (not a section header): regular weight with a leading history icon, so
// it reads as a click target distinct from the semibold "Input:"/"Output:" labels in ToolDetail.
const useStyles = createStyles(() => ({
  versionLink: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    display: 'inline-flex',
    gap: 6,
  },
}));

interface MemoryVersionLinkProps {
  // The first memory file referenced by the tool output.
  page: string;
  // The owning agent whose memory viewer the link opens.
  routineId: string;
  // The whole-memory set version API id from the tool output to open the viewer at.
  versionId: string;
}

/**
 * A single link (rendered at the top of the tool detail) that deep-links to this version of the
 * agent's memory in its viewer.
 */
export function MemoryVersionLink({page, routineId, versionId}: MemoryVersionLinkProps): JSX.Element {
  const {classes} = useStyles();

  const handleOpen = useCallback(() => {
    const url = chatSettingsModalUrl(ChatSettingsTab.AGENTS, {
      selectedItemId: routineId,
      memoryTarget: {versionId, page},
    });
    // Dismiss the tool-call activity modal so the settings modal is visible underneath.
    LegacyModalContainer.close(ACTIVITY_MODAL_KEY);
    navigateToURL(url);
  }, [page, routineId, versionId]);

  return (
    <Anchor className={classes.versionLink} component="button" onClick={handleOpen} size="sm" type="button">
      <BenchlingIcon aria-hidden={true} icon="history" />
      View this memory version
    </Anchor>
  );
}
